'use client';

import { useTranslation } from '@/app/i18n/client';
import { cn } from '@/lib/utils';
import { EnumExperienceType, Session } from '@/utils/types';
import { ChangeEvent, useEffect, useState } from 'react';

interface SessionButtonsProps {
  options: Session[];
  onSelect: (option?: Session) => void;
  disabled?: boolean;
  lng: string;
  experienceType: EnumExperienceType;
  className?: string;
}

type SessionWithOverlap = Session & { overlap: boolean; totalTicketsAvailable: number; configurationId: string };

export const SessionButtons: React.FC<SessionButtonsProps> = ({
  lng,
  options,
  onSelect,
  disabled,
  experienceType,
  className,
}) => {
  const { t } = useTranslation(lng, 'tickets');
  const [selectedOption, setSelectedOption] = useState<SessionWithOverlap>();

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedSession: SessionWithOverlap = JSON.parse(event.target.value);
    setSelectedOption(selectedSession);

    const session = options.find((s) => s.startsAt === selectedSession.startsAt);

    onSelect(session);
  };

  useEffect(() => {
    setSelectedOption(undefined);
  }, [options]);

  const mergeSessions = (sessions: SessionWithOverlap[]) => {
    const mergedSessions: Record<string, SessionWithOverlap> = {};

    sessions.forEach((session: SessionWithOverlap) => {
      if (session?.startsAt) {
        const key = `${session.startsAt}${session.configurationId}`;
        const alreadyExistOrHasOverlap =
          !mergedSessions[key] || (mergedSessions[key] && !mergedSessions[key].overlap && session.overlap);

        if (alreadyExistOrHasOverlap) mergedSessions[key] = { ...session };
      }
    });

    return Object.values(mergedSessions);
  };

  const optionsWithOverlap = options.map((session) => {
    const foundWithOverlap = options.find(({ startsAt, overlap }) => {
      const isSameTimestamp = startsAt === session.startsAt;

      return isSameTimestamp && overlap;
    });

    if (foundWithOverlap) {
      return {
        ...session,
        startsAt: session.startsAt,
        overlap: true,
        totalTicketsAvailable: foundWithOverlap.totalTicketsAvailable,
        configurationId: foundWithOverlap.configurationId,
      };
    } else {
      const foundWithoutOverlap = options.find(({ startsAt }) => startsAt === session.startsAt);

      if (foundWithoutOverlap) {
        return {
          ...session,
          startsAt: session.startsAt,
          overlap: false,
          totalTicketsAvailable: foundWithoutOverlap.totalTicketsAvailable,
          configurationId: foundWithoutOverlap.configurationId,
        };
      }
    }
  }) as SessionWithOverlap[];

  const mergedSessions = mergeSessions(optionsWithOverlap);

  const notShowRemainingTickets = [
    EnumExperienceType.MASP_EVENTOS,
    EnumExperienceType.MASP_FESTAS,
    EnumExperienceType.MASP_FESTAS_PISTA,
    EnumExperienceType.MASP_ESPETACULOS_E_EVENTOS_COM_LUGAR,
    EnumExperienceType.MASP_FESTAS,
    EnumExperienceType.MASP_FESTAS_PISTA,
    EnumExperienceType.VERNISSAGE,
  ];
  const showRemainingTickets = !notShowRemainingTickets.includes(experienceType);

  return (
    <ul
      data-testid='session-buttons'
      className={cn('flex flex-col space-y-4 max-sm:grid max-sm:grid-cols-2 max-sm:gap-4 max-sm:space-y-0', className)}>
      {mergedSessions
        .filter((option: Session) => option.available)
        .map(({ isSoldOut, ...option }: Session, index) => {
          const date = option.startsAt;

          const transformDate = new Intl.DateTimeFormat('pt-BR', {
            timeZone: 'America/Sao_Paulo',
            hour: 'numeric',
            minute: 'numeric',
          }).format(date);

          return (
            <li
              key={option.startsAt}
              data-testid='session-button'
              data-index={index}
              aria-disabled={!!disabled || isSoldOut}
              className={`flex w-full items-center justify-start border border-zinc-400 ${
                selectedOption?.startsAt === option.startsAt ? 'border-primary' : 'border-foreground'
              }`}
              style={{
                pointerEvents: disabled || isSoldOut ? 'none' : 'auto',
                opacity: disabled || isSoldOut ? 0.5 : 1,
                cursor: disabled || isSoldOut ? 'not-allowed' : 'pointer',
                backgroundColor: selectedOption?.startsAt === option.startsAt ? '#E30613' : 'transparent',
              }}>
              <label
                className={`flex h-full w-full p-4 max-sm:flex-col ${
                  isSoldOut ? 'cursor-not-allowed' : 'cursor-pointer'
                } items-center justify-between`}>
                <input
                  disabled={!!disabled || isSoldOut}
                  type='radio'
                  className='hidden'
                  value={JSON.stringify(option)}
                  checked={selectedOption?.startsAt === option.startsAt}
                  onChange={handleChange}
                  aria-label={`${t('session')} ${transformDate}${isSoldOut ? ` - ${t('soldOut')}` : ''}`}
                />
                <span
                  className={`flex w-fit items-center justify-center text-xl font-medium ${
                    selectedOption?.startsAt === option.startsAt ? 'text-white' : 'text-foreground'
                  }`}>
                  {transformDate}
                </span>

                {showRemainingTickets && !isSoldOut && (
                  <span
                    className={`text-center font-light ${
                      selectedOption?.startsAt === option.startsAt ? 'text-white' : 'text-zinc-600'
                    }`}>
                    {t('remaining')} {option.totalTicketsAvailable}
                  </span>
                )}

                {isSoldOut && (
                  <span
                    className='px-2 py-1 text-center font-light uppercase text-white'
                    style={{
                      pointerEvents: isSoldOut ? 'none' : 'auto',
                      opacity: isSoldOut ? 0.5 : 1,
                      cursor: isSoldOut ? 'not-allowed' : 'pointer',
                      backgroundColor: isSoldOut ? '#ba281b' : 'white',
                      color: isSoldOut ? 'white' : 'black',
                    }}>
                    {t('soldOut')}
                  </span>
                )}
              </label>
            </li>
          );
        })}
    </ul>
  );
};
